import React, { useEffect } from "react";

import { useGetPatientInteractionsActions } from "~/providers/hooks";
import { usePreviewStore } from "~/providers/stores/usePreviewStore";
import { getConsultationSummaryV3Data } from "~/providers/utils/consultationRequests/patientPreview/getConsultationSummaryV3Data";
import { PatientIntakeConsultationSummaryV3 } from "~/providers/utils/types/patientIntakePreviewTypes";
import {
  PatientScreeningConsultationSummaryV3,
  SUMMARY_TYPE,
} from "~/providers/utils/types/patientPreviewTypes";
import { PatientPreviewTextInfo } from "../common";
import { Fillm } from "../common/Fillm";
import { FunctionalDomainsV2 } from "../v2/functionalDomains";
import { PotentialAreasConcern } from "../v2/PotentialAreasConcernV2";
import { PatientPreviewSelfReportedScoresV2 } from "../v2/selfReportedScores";
import { PatientPreviewCurrentMedicationsV2 } from "../v2/selfReportedScores/PatientPreviewCurrentMedicationsV2";
import { HealthHistoryV3 } from "./HealthHistoryV3";
import { PersonalLifeHistoryV3 } from "./PersonalLifeHistoryV3";
import { TreatmentRecommendationsV3 } from "./TreatmentRecommendationsV3";

interface PreviewSectionV2Props {
  screeningSummary: PatientScreeningConsultationSummaryV3;
  patientId: string;
  intakeSummary?: PatientIntakeConsultationSummaryV3;
}

export const PreviewSectionsV3 = ({
  patientId,
  screeningSummary,
  intakeSummary,
}: PreviewSectionV2Props) => {
  const {
    screeningSummaryId,
    intakeSummaryId,
    isSummaryLiked,
    isTreatmentRecommendationsLiked,
    medications,
    scores,
    treatmentRecommendations,
    caseSummary,
    functionalDomains,
    healthHistory,
    personalLifeHistory,
    potentialAreasOfConcern,
    ai_informant_conversation_history,
    allergies,
    fillm,
  } = getConsultationSummaryV3Data({ screeningSummary, intakeSummary });

  const { showRateButtons, summaryType, handleRateSummary, isRatePending } =
    useGetPatientInteractionsActions({
      patientId,
      screeningSummaryId,
      intakeSummaryId,
    });

  const { patientId: storePatientId, setPatientId, reset } = usePreviewStore();

  useEffect(() => {
    if (!storePatientId || storePatientId != patientId) {
      reset();
      setPatientId(patientId);
    }
  }, [patientId, storePatientId, setPatientId, reset]);

  return (
    <>
      <PatientPreviewTextInfo
        showRateButtons={showRateButtons}
        title="Case summary"
        info={caseSummary}
        summaryType={summaryType}
        onRate={handleRateSummary}
        isLike={isSummaryLiked}
        isRatePending={isRatePending}
      />

      <PatientPreviewCurrentMedicationsV2
        medications={medications}
        allergies={allergies}
      />

      {healthHistory && <HealthHistoryV3 {...healthHistory} />}

      {personalLifeHistory && (
        <PersonalLifeHistoryV3 {...personalLifeHistory} />
      )}

      {functionalDomains && (
        <FunctionalDomainsV2 functionalDomains={functionalDomains} />
      )}

      {ai_informant_conversation_history && (
        <Fillm
          aiConversationHistory={ai_informant_conversation_history}
          fillm={fillm}
        />
      )}

      {treatmentRecommendations && (
        <TreatmentRecommendationsV3
          treatmentRecommendations={treatmentRecommendations}
          summaryType={SUMMARY_TYPE.treatment_recommendations}
          onRate={handleRateSummary}
          isLike={isTreatmentRecommendationsLiked}
          isRatePending={isRatePending}
        />
      )}

      <PotentialAreasConcern
        potentialAreasOfConcern={potentialAreasOfConcern}
      />

      <PatientPreviewSelfReportedScoresV2 scores={scores} />
    </>
  );
};
